import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Shield, 
  ShieldAlert, 
  ShieldCheck, 
  AlertTriangle, 
  Eye, 
  CheckCircle, 
  Loader2,
  RefreshCw,
  Monitor,
  Mail,
  Globe,
  Clock,
  Filter
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AbuseAlertsPage = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertDetails, setAlertDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [filter, setFilter] = useState('unresolved'); // 'all', 'unresolved', 'resolved'
  const [severityFilter, setSeverityFilter] = useState('all'); // 'all', 'high', 'medium', 'low'

  useEffect(() => {
    fetchAlerts();
  }, [filter, severityFilter]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filter === 'unresolved') params.append('resolved', 'false');
      else if (filter === 'resolved') params.append('resolved', 'true');
      
      if (severityFilter !== 'all') params.append('severity', severityFilter);
      
      const response = await axios.get(`${API}/admin/abuse-alerts?${params.toString()}`);
      setAlerts(response.data.alerts);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error loading alerts:', error);
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertDetails = async (alertId) => {
    try {
      setDetailsLoading(true);
      const response = await axios.get(`${API}/admin/abuse-alerts/${alertId}`);
      setAlertDetails(response.data);
    } catch (error) {
      console.error('Error loading alert details:', error);
      toast.error('Erreur lors du chargement des détails');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewDetails = (alert) => {
    setSelectedAlert(alert);
    fetchAlertDetails(alert.id);
  };

  const handleResolve = async () => {
    if (!selectedAlert) return;
    
    try {
      await axios.post(`${API}/admin/abuse-alerts/${selectedAlert.id}/resolve`, null, {
        params: { notes: resolveNotes }
      });
      toast.success('Alerte marquée comme résolue');
      setSelectedAlert(null);
      setAlertDetails(null);
      setResolveNotes('');
      fetchAlerts();
    } catch (error) {
      toast.error('Erreur lors de la résolution');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'medium': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high': return <ShieldAlert className="w-4 h-4" />;
      case 'medium': return <AlertTriangle className="w-4 h-4" />;
      case 'low': return <Shield className="w-4 h-4" />;
      default: return <Shield className="w-4 h-4" />;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/search')}
              variant="ghost"
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <ShieldAlert className="w-6 h-6 text-red-400" />
                Alertes Anti-Abus
              </h1>
              <p className="text-gray-400 text-sm">Surveillance des tentatives d&apos;abus d&apos;essai gratuit</p>
            </div>
          </div>
          <Button onClick={fetchAlerts} variant="outline" className="border-gray-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="glass rounded-xl p-4 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Non résolues</p>
                <p className="text-2xl font-bold">{stats.total_unresolved || 0}</p>
              </div>
              <ShieldAlert className="w-8 h-8 text-red-400" />
            </div>
          </div>
          <div className="glass rounded-xl p-4 border border-red-900/30 bg-red-900/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Haute sévérité</p>
                <p className="text-2xl font-bold text-red-400">{stats.high_severity || 0}</p>
              </div>
              <ShieldAlert className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <div className="glass rounded-xl p-4 border border-amber-900/30 bg-amber-900/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Moyenne sévérité</p>
                <p className="text-2xl font-bold text-amber-400">{stats.medium_severity || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-500" />
            </div>
          </div>
          <div className="glass rounded-xl p-4 border border-blue-900/30 bg-blue-900/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Basse sévérité</p>
                <p className="text-2xl font-bold text-blue-400">{stats.low_severity || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40 bg-gray-800 border-gray-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="unresolved">Non résolues</SelectItem>
                <SelectItem value="resolved">Résolues</SelectItem>
                <SelectItem value="all">Toutes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-40 bg-gray-800 border-gray-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all">Toutes sévérités</SelectItem>
              <SelectItem value="high">Haute</SelectItem>
              <SelectItem value="medium">Moyenne</SelectItem>
              <SelectItem value="low">Basse</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Alerts List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="glass rounded-xl p-12 border border-gray-800 text-center">
            <ShieldCheck className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Aucune alerte</h3>
            <p className="text-gray-400">Pas d&apos;abus détecté pour le moment</p>
          </div>
        ) : (
          <div className="glass rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Sévérité</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">IP</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Raisons</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {alerts.map((alert) => (
                    <tr key={alert.id} className={`hover:bg-gray-800/30 ${alert.resolved ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full border flex items-center gap-1 w-fit ${getSeverityColor(alert.severity)}`}>
                          {getSeverityIcon(alert.severity)}
                          {alert.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <span className="text-sm">{alert.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-mono">{alert.ip_address}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-400">
                          {alert.reasons.length} raison(s)
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Clock className="w-4 h-4" />
                          {formatDate(alert.timestamp)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetails(alert)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Détails
                          </Button>
                          {!alert.resolved && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedAlert(alert);
                                handleResolve();
                              }}
                              className="text-green-400 hover:text-green-300"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Résoudre
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Alert Details Dialog */}
        <Dialog open={!!selectedAlert && !!alertDetails} onOpenChange={(open) => !open && setSelectedAlert(null)}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-red-400" />
                Détails de l&apos;alerte
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Analyse complète de la tentative d&apos;abus détectée
              </DialogDescription>
            </DialogHeader>

            {detailsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : alertDetails && (
              <div className="space-y-6">
                {/* Alert Summary */}
                <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    Résumé
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Email:</span>
                      <span className="ml-2">{alertDetails.alert.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">IP:</span>
                      <span className="ml-2 font-mono">{alertDetails.alert.ip_address}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Fingerprint:</span>
                      <span className="ml-2 font-mono text-xs">{alertDetails.alert.fingerprint || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Sévérité:</span>
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs ${getSeverityColor(alertDetails.alert.severity)}`}>
                        {alertDetails.alert.severity}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Reasons */}
                <div className="p-4 rounded-lg bg-red-900/20 border border-red-900/30">
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-red-400">
                    <ShieldAlert className="w-4 h-4" />
                    Raisons de détection
                  </h4>
                  <ul className="space-y-2">
                    {alertDetails.alert.reasons.map((reason, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-red-400">•</span>
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Same Email Attempts */}
                {alertDetails.same_email_attempts?.length > 0 && (
                  <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-blue-400" />
                      Tentatives avec email similaire ({alertDetails.same_email_attempts.length})
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {alertDetails.same_email_attempts.map((attempt) => (
                        <div key={attempt.id} className="flex justify-between text-sm p-2 bg-gray-900/50 rounded">
                          <span>{attempt.email}</span>
                          <span className="text-gray-400">{formatDate(attempt.timestamp)}</span>
                          <span className={attempt.status === 'blocked' ? 'text-red-400' : 'text-green-400'}>
                            {attempt.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Same IP Attempts */}
                {alertDetails.same_ip_attempts?.length > 0 && (
                  <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-amber-400" />
                      Tentatives depuis même IP ({alertDetails.same_ip_attempts.length})
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {alertDetails.same_ip_attempts.map((attempt) => (
                        <div key={attempt.id} className="flex justify-between text-sm p-2 bg-gray-900/50 rounded">
                          <span>{attempt.email}</span>
                          <span className="text-gray-400">{formatDate(attempt.timestamp)}</span>
                          <span className={attempt.status === 'blocked' ? 'text-red-400' : 'text-green-400'}>
                            {attempt.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resolution */}
                {!alertDetails.alert.resolved && (
                  <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                    <h4 className="font-semibold mb-3">Résoudre l&apos;alerte</h4>
                    <Input
                      placeholder="Notes (optionnel)"
                      value={resolveNotes}
                      onChange={(e) => setResolveNotes(e.target.value)}
                      className="bg-gray-800 border-gray-700 mb-3"
                    />
                    <Button onClick={handleResolve} className="bg-green-600 hover:bg-green-700">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Marquer comme résolu
                    </Button>
                  </div>
                )}

                {alertDetails.alert.resolved && (
                  <div className="p-4 rounded-lg bg-green-900/20 border border-green-900/30">
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">Résolu</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-2">
                      Par {alertDetails.alert.resolved_by} le {formatDate(alertDetails.alert.resolved_at)}
                    </p>
                    {alertDetails.alert.notes && (
                      <p className="text-sm mt-2">Notes: {alertDetails.alert.notes}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AbuseAlertsPage;
